/**
 * Correlation ids for premium-gate and billing traces.
 *
 * One id per browser session is attached to every premium-gate, entitlement and
 * checkout event, and forwarded into Stripe/StoreKit metadata so the webhook
 * handlers can log the same id. Searching one id in PostHog, Sentry and the
 * server logs then reconstructs a full "tapped Subscribe → webhook activated
 * premium" trace. Contains no personal data.
 */
const KEY = "inquira_correlation_id";

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `cid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable per-session correlation id (browser). Safe during SSR. */
export function getCorrelationId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = randomId();
    window.sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    // Private mode / storage disabled: keep tracing within the page lifetime.
    const w = window as unknown as { __inquiraCid?: string };
    w.__inquiraCid ??= randomId();
    return w.__inquiraCid;
  }
}

/** Starts a fresh trace (e.g. a new checkout attempt) and returns the id. */
export function newCorrelationId(): string {
  const id = randomId();
  try {
    if (typeof window !== "undefined") window.sessionStorage.setItem(KEY, id);
  } catch {
    /* noop */
  }
  return id;
}

/**
 * Server-side correlation id for an inbound billing event. Prefers the
 * client-supplied id carried in provider metadata, then the provider's own
 * event/transaction id, so both ends of the trace share a value.
 */
export function serverCorrelationId(
  fromMetadata?: string | null,
  providerEventId?: string | null,
): string {
  return fromMetadata || providerEventId || `srv_${randomId()}`;
}
